import React from 'react'
import { isMobile } from 'react-device-detect'
import {
  UNSAFE_LocationContext as LocationContext,
  UNSAFE_NavigationContext as NavigationContext,
} from 'react-router-dom'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import {
  DcBreadcrumbs,
  DcButton,
  DcFlexBox,
  PixelPageContainer,
  PixelSpinner,
} from 'pixel-base'
import FinanceDemoBanner from './FinanceDemoBanner'
import { FinanceDemoModeProvider } from '../hooks/useFinanceDemoMode'
import FinanceTabs from './foundation/FinanceTabs'

interface BreadcrumbItem {
  name: string
  active: boolean
}

interface Action {
  label: string
  onClick: () => void
}

interface Props {
  title: string
  breadcrumbs?: BreadcrumbItem[]
  isLoading?: boolean
  primaryAction?: Action
  children: React.ReactNode
}

interface NavItem {
  label: string
  path: string
  match: (pathname: string) => boolean
}

const primarySections: (NavItem & { key: string })[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/finance',
    match: (pathname) => pathname === '/finance',
  },
  {
    key: 'ledger',
    label: 'Ledger',
    path: '/finance/accounts',
    match: (pathname) =>
      ['/finance/accounts', '/finance/journal-entries'].some((prefix) =>
        pathname.startsWith(prefix),
      ),
  },
  {
    key: 'documents',
    label: 'Documents',
    path: '/finance/invoices',
    match: (pathname) =>
      [
        '/finance/expenses',
        '/finance/invoices',
        '/finance/vendor-bills',
        '/finance/payments',
        '/finance/reconciliation',
      ].some((prefix) => pathname.startsWith(prefix)),
  },
  {
    key: 'reports',
    label: 'Reports',
    path: '/finance/reports',
    match: (pathname) => pathname.startsWith('/finance/reports'),
  },
  {
    key: 'settings',
    label: 'Settings',
    path: '/finance/settings',
    match: (pathname) =>
      [
        '/finance/settings',
        '/finance/journals',
        '/finance/periods',
        '/finance/demo-data',
        '/finance/partners',
        '/finance/cost-centers',
      ].some((prefix) => pathname.startsWith(prefix)),
  },
]

const secondarySections: Record<string, NavItem[]> = {
  ledger: [
    {
      label: 'Chart of Accounts',
      path: '/finance/accounts',
      match: (pathname) => pathname.startsWith('/finance/accounts'),
    },
    {
      label: 'Entries',
      path: '/finance/journal-entries',
      match: (pathname) => pathname.startsWith('/finance/journal-entries'),
    },
  ],
  documents: [
    {
      label: 'Expenses',
      path: '/finance/expenses',
      match: (pathname) => pathname.startsWith('/finance/expenses'),
    },
    {
      label: 'Invoices',
      path: '/finance/invoices',
      match: (pathname) => pathname.startsWith('/finance/invoices'),
    },
    {
      label: 'Vendor Bills',
      path: '/finance/vendor-bills',
      match: (pathname) => pathname.startsWith('/finance/vendor-bills'),
    },
    {
      label: 'Payments',
      path: '/finance/payments',
      match: (pathname) => pathname.startsWith('/finance/payments'),
    },
    {
      label: 'Reconciliation',
      path: '/finance/reconciliation',
      match: (pathname) => pathname.startsWith('/finance/reconciliation'),
    },
  ],
  settings: [
    {
      label: 'Settings',
      path: '/finance/settings',
      match: (pathname) => pathname === '/finance/settings',
    },
    {
      label: 'Journals',
      path: '/finance/journals',
      match: (pathname) => pathname.startsWith('/finance/journals'),
    },
    {
      label: 'Periods',
      path: '/finance/periods',
      match: (pathname) => pathname.startsWith('/finance/periods'),
    },
    {
      label: 'Partners',
      path: '/finance/partners',
      match: (pathname) => pathname.startsWith('/finance/partners'),
    },
    {
      label: 'Cost Centers',
      path: '/finance/cost-centers',
      match: (pathname) => pathname.startsWith('/finance/cost-centers'),
    },
    {
      label: 'Bank Accounts',
      path: '/finance/settings/bank-accounts',
      match: (pathname) =>
        pathname.startsWith('/finance/settings/bank-accounts'),
    },
    {
      label: 'Demo Data',
      path: '/finance/demo-data',
      match: (pathname) => pathname.startsWith('/finance/demo-data'),
    },
  ],
}

const FinancePageShell: React.FC<Props> = ({
  title,
  breadcrumbs,
  isLoading = false,
  primaryAction,
  children,
}) => {
  const locationContext = React.useContext(LocationContext)
  const navigationContext = React.useContext(NavigationContext)
  const pathname = locationContext?.location?.pathname || '/finance'
  const onNavigate = navigationContext?.navigator
    ? (path: string) => navigationContext.navigator.push(path)
    : null

  const data = breadcrumbs || [
    { name: 'Finance', active: false },
    { name: title, active: true },
  ]

  const activePrimary =
    primarySections.find((section) => section.match(pathname)) ||
    primarySections[0]
  const activeSecondary = secondarySections[activePrimary.key] || []

  return (
    <FinanceDemoModeProvider>
      <>
        <PixelSpinner isShow={isLoading} />
        <DcFlexBox
          height="60px"
          fontSize="16px"
          style={{
            fontSize: '16px',
            padding: '8px 0px',
            borderBottom: '1px solid #e5e5e5',
            backgroundColor: 'white',
          }}
        >
          <DcFlexBox
            justifyContent="space-between"
            alignItems="center"
            padding=".5rem 20px"
            style={{ width: '100%' }}
          >
            <DcBreadcrumbs fontSize="16px" data={data} />
            {navigationContext?.navigator ? <ProfileSection /> : null}
          </DcFlexBox>
        </DcFlexBox>
        <PixelPageContainer
          padding="0px"
          style={{ overflow: 'hidden' }}
          isMobile={isMobile}
        >
          {activeSecondary.length > 0 && (
            <FinanceTabs
              size="sm"
              compact
              items={activeSecondary.map((item) => ({
                label: item.label,
                path: item.path,
                active: item.match(pathname),
                onClick: () => onNavigate?.(item.path),
              }))}
            />
          )}
          <FinanceDemoBanner />
          {primaryAction && (
            <DcFlexBox justifyContent="flex-end" padding="12px 20px 0 20px">
              <DcButton size="sm" onClick={primaryAction.onClick}>
                {primaryAction.label}
              </DcButton>
            </DcFlexBox>
          )}
          <div
            style={{
              padding: '20px',
              overflow: 'auto',
              maxHeight: primaryAction
                ? 'calc(100vh - 180px)'
                : 'calc(100vh - 132px)',
            }}
          >
            {children}
          </div>
        </PixelPageContainer>
      </>
    </FinanceDemoModeProvider>
  )
}

export default FinancePageShell
